import * as z from 'zod'

import { useQuery } from '@tanstack/react-query'
import { PublicUser } from '~/app/api/users/public-user-schema'
import { isDev, isPreview } from '~/constants/routing'
import { invariant } from '@epic-web/invariant'
import { useSelectedTeam } from './use-selected-team'

export type PublicUserData = PublicUser & { isMe?: boolean }

/**
 * fetches user data from api
 * for "me", it'll get it fresh from useClerk.
 * if `null`, will use the current authenticated user
 */
export function useTeamMembers() {
  const teamId = useSelectedTeam().team?.id

  const query = useQuery<PublicUserData[] | null>({
    queryKey: ['team-member-list', teamId],
    queryFn: async () => {
      invariant(teamId, 'not enabled')

      const res = await fetch(`/api/users?team-id=${teamId}`)
      const data = await res.json()
      const parsedUsers = z.array(PublicUser).safeParse(data)

      if (!parsedUsers.success) {
        if (isPreview || isDev) return null
        throw new Error(`Invalid response data: ${parsedUsers.error.message}`)
      }
      if (!parsedUsers.data) {
        throw new Error('No data')
      }

      return parsedUsers.data
    },
    enabled: !!teamId,
    staleTime: Infinity,
  })

  return query
}
